import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { MetaAdWithInsights } from 'services/api/metaInsights.api';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW } from 'styles/tokens';

import {
  type DerivedKpiInputs,
  DerivedKpis,
  LeadCounts,
  MetaInsightsKpiGrid,
  parseSpend,
  SectionHeader,
  ViewLeadsLink,
} from './metaDrillDownPrimitives';

interface Props {
  ad: MetaAdWithInsights;
  currency: string | null;
  businessUnitId?: number;
  since?: string;
  until?: string;
}

const MetaAdSheet: React.FC<Props> = ({ ad, currency, businessUnitId, since, until }) => {
  const insights = ad.insights;
  const adSpend = parseSpend(insights?.spend);
  const leads = ad.leadsStats;

  const derivedKpiInputs: DerivedKpiInputs = {
    spend: adSpend,
    clicks: parseSpend(insights?.clicks),
    contactados: leads?.leadsWithClientResponses ?? null,
    inCrm: leads?.leadsUploadedToCRM ?? null,
    currency,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2.5,
          borderRadius: RADIUS_ITEM,
          bgcolor: SURFACE_CONTAINER_LOW,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.55), fontSize: '0.72rem' }}>
            ID:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', color: NAVY }}>
              {ad.id}
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box>
        <SectionHeader title="Rendimiento en Meta Ads" source="meta" />
        <MetaInsightsKpiGrid insights={insights ?? null} currency={currency} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SectionHeader title="Leads en nuestro CRM" source="crm" />
        <LeadCounts
          inputs={{
            contactados: leads?.leadsWithClientResponses ?? null,
            inCrm: leads?.leadsUploadedToCRM ?? null,
          }}
        />
        <DerivedKpis inputs={derivedKpiInputs} />
      </Box>

      <ViewLeadsLink
        field="metaAdExternalId"
        value={ad.id}
        label="Ver leads de este anuncio"
        businessUnitId={businessUnitId}
        since={since}
        until={until}
      />
    </Box>
  );
};

export default MetaAdSheet;
